import { useEffect, useLayoutEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

// How long a toast stays up before it slides back down
export const toastDurationMs = 3000;

// The song menu sheet's motion (slide_menus.tsx)
const slideMs = 250;

// A confirmation for an action the screen itself does not show, such as a church key saved while the row it was set
// on goes quiet (issue #4221). Callable from anywhere: the one <ToastHost /> in the root page shows it. A toast that
// arrives while another is up replaces its text and restarts its time.
export function showToast(message: string) {
  for (const listener of toastListeners) {
    listener(message);
  }
}

const toastListeners = new Set<(message: string) => void>();

interface ToastState {
  message: string;
  // a new id for every showToast, so a replacement restarts the timer even when its text is the same
  id: number;
  leaving: boolean;
}

// durationMs is how long a toast stays up; one that is not finite (Infinity) sets no timer, so the toast stays until
// it is tapped or replaced, which is what a snapshot needs
export const ToastHost = ({ durationMs = toastDurationMs }: { durationMs?: number }) => {
  const [toast, setToast] = useState<ToastState | undefined>();

  // A layout effect, so the host listens as soon as it is mounted: a passive effect can run after the next task, and a
  // showToast made in between (a Storybook play function right after render) would reach no one
  useLayoutEffect(() => {
    const listener = (message: string) =>
      setToast((current) => ({ message, id: (current?.id ?? 0) + 1, leaving: false }));
    toastListeners.add(listener);
    return () => {
      toastListeners.delete(listener);
    };
  }, []);

  const id = toast?.id;
  useEffect(() => {
    if (id === undefined || !Number.isFinite(durationMs)) {
      return;
    }
    const leaveTimer = setTimeout(() => setToast((current) => current && { ...current, leaving: true }), durationMs);
    const removeTimer = setTimeout(() => setToast(undefined), durationMs + slideMs);
    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(removeTimer);
    };
  }, [id, durationMs]);

  // The live region stays mounted with or without a toast: a screen reader often announces only a change to a live
  // region already in the page, so a region arriving together with its text may go unsaid
  return (
    <ToastPosition role="status">
      {toast && (
        <ToastPill $leaving={toast.leaving} onClick={() => setToast(undefined)}>
          {toast.message}
        </ToastPill>
      )}
    </ToastPosition>
  );
};

// Distance from the bottom of the screen, above the home indicator. It clears the song menu's Transpose Key row, the
// row a toast confirms: the last content of a sheet pinned to the screen's bottom, it reaches about 106 px up (20 px
// padding, 46 px control, 5 px margin, 30 px Reset, 5 px margin), and a toast over it would hide the new key and
// swallow taps meant for the control.
const bottomOffset = 'calc(120px + env(safe-area-inset-bottom))';

const slideUp = keyframes`
  from { transform: translateY(calc(100% + ${bottomOffset})); }
  to { transform: translateY(0); }
`;

const slideDown = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(calc(100% + ${bottomOffset})); }
`;

// spans the screen's width only to centre the pill; it lets every tap through to the page beneath
const ToastPosition = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: ${bottomOffset};
  display: flex;
  justify-content: center;
  pointer-events: none;
  /* above the player (10), the song menu sheet (190, 200) and the profile drawer (300, 301) */
  z-index: 400;
`;

const ToastPill = styled.div<{ $leaving: boolean }>`
  max-width: 90vw;
  box-sizing: border-box;
  padding: 10px 18px;
  border-radius: 12px;
  background-color: var(--color-text);
  color: var(--color-background);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 15px;
  text-align: center;
  overflow-wrap: anywhere;
  pointer-events: auto;
  cursor: pointer;
  ${({ $leaving }) =>
    $leaving
      ? css`
          animation: ${slideDown} ${slideMs}ms forwards;
        `
      : css`
          animation: ${slideUp} ${slideMs}ms;
        `}
`;
